/**
 * "Where the market prices you" (screens §P9.4): each bank's move over the year on a map of return on tangible equity (across) against price to tangible book (up), your twin's year-end
 * position, and the curve the market's own formula draws between them. Pure; every figure is a view the web already receives.
 */
import type { MarketView, RoundReport, TeamState } from "@bsim/contracts";
import type { KpiSnapshot } from "@bsim/domain";

export interface MapPoint { readonly rotce: number; readonly ptbv: number }
export interface MapBank { readonly teamId: string; readonly name: string; readonly isYou: boolean; readonly from: MapPoint; readonly to: MapPoint }

const pointOf = (kpis: KpiSnapshot): MapPoint | null => (kpis.rotce === undefined || kpis.ptbv === undefined ? null : { rotce: kpis.rotce, ptbv: kpis.ptbv });

/**
 * Each bank's arrow: from where it stood at the end of last year (`publicMarket` through round n − 1; through round 0 is SimStart) to its year-end (through round n).
 * If the market has not answered for the start of the game, every bank starts it identical (SimStart is one balance sheet for all six), so `kpiHistory[0]` stands for each.
 */
export function marketArrows(args: { readonly marketThrough: ReadonlyMap<number, MarketView>; readonly kpiHistory: TeamState["kpiHistory"]; readonly roundNo: number; readonly myTeamId: string }): MapBank[] {
  const { marketThrough, kpiHistory, roundNo, myTeamId } = args;
  const end = marketThrough.get(roundNo);
  if (end === undefined) return [];
  const before = marketThrough.get(roundNo - 1);
  const opening = pointOf(kpiHistory.find((h) => h.roundNo === 0)?.kpis ?? {});
  return end.banks.flatMap((b) => {
    const to = pointOf(b.kpis);
    const from = before === undefined ? (roundNo === 1 ? opening : null) : pointOf(before.banks.find((x) => x.teamId === b.teamId)?.kpis ?? {});
    return to === null || from === null ? [] : [{ teamId: b.teamId, name: b.name, isYou: b.teamId === myTeamId, from, to }];
  });
}

/** Your twin at this year-end: your KPI less the report's gap to it (`attribution.vsAutopilot`), as `priceLine` and Financials derive it. */
export function twinPoint(report: RoundReport): MapPoint | null {
  const gap = report.attribution.vsAutopilot;
  const mine = pointOf(report.kpis);
  return mine === null || gap.rotce === undefined || gap.ptbv === undefined ? null : { rotce: mine.rotce - gap.rotce, ptbv: mine.ptbv - gap.ptbv };
}

/** What the curve needs: the multiple's ceiling and the least spread between the cost of equity and growth (content), and your bank's growth and cost of equity (the report's `valuation`). */
export interface CurveInputs { readonly ptbvMax: number; readonly coeMinusGFloorRate: number; readonly gRate: number; readonly coeRate: number }

/** 10.1 E8: the multiple the market pays a bank earning `rotceRate`: (ROTCE − g) ÷ max(COE − g, floor), held between 0 and the ceiling. */
export const pricingMultiple = (rotceRate: number, c: CurveInputs): number => Math.min(c.ptbvMax, Math.max(0, (rotceRate - c.gRate) / Math.max(c.coeRate - c.gRate, c.coeMinusGFloorRate)));

/** The curve's inputs from the content's valuation parameters and this year's own valuation (your growth and cost of equity); null while the parameters are unknown. */
export const curveInputsOf = (params: { readonly ptbvMax: number; readonly coeMinusGFloorRate: number } | undefined, valuation: { readonly gRate: number; readonly coeRate: number }): CurveInputs | null =>
  params === undefined ? null : { ptbvMax: params.ptbvMax, coeMinusGFloorRate: params.coeMinusGFloorRate, gRate: valuation.gRate, coeRate: valuation.coeRate };

/** The map's frame: fixed, so a year's move is a short step and no bank leaves it (DESIGN-RESULTS §4). */
export const MAP_FRAME = { x: [6, 22] as const, y: [0.4, 2.0] as const, xTicks: [6, 10, 14, 18, 22] as const, yTicks: [0.5, 1.0, 1.5, 2.0] as const };

/** A point in the frame's units (return in percent, multiple in x), pinned to the edge when it falls outside; `off` says it did. */
export function pinToFrame(rotceRate: number, ptbv: number): { readonly x: number; readonly y: number; readonly off: boolean } {
  const x = rotceRate * 100, [xl, xh] = MAP_FRAME.x, [yl, yh] = MAP_FRAME.y;
  const px = Math.min(xh, Math.max(xl, x)), py = Math.min(yh, Math.max(yl, ptbv));
  return { x: px, y: py, off: px !== x || py !== ptbv };
}
